import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import useColors from "api/hooks/useColors";
import TextButton from "components/buttons/TextButton";
import StatusChip from "components/chips/StatusChip";
import CustomTooltip from "components/CustomTooltip";
import FilterMenu from "components/filters/FilterMenu";
import SearchInput from "components/filters/SearchInput";
import StyledDataGrid from "components/StyledDataGrid";
import { useState } from "react";
import { getStatusColor as getStatusIndicatorColor } from "scenes/posture/controls/components/EntitiesSection/utils";
import ChipColor from "scenes/remediation/plan/components/ChipColor";
import IntegrationImage from "scenes/stack/integrations/components/IntegrationImage";
import { RemediationTypeChip } from "utils/chipsData";

const ControlsTableWidget = ({ controls, isLoading }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    toolFilter: {},
    severityFilter: {},
    statusFilter: {},
    searchString: "",
  });
  const [customSeverityOnly, setCustomSeverityOnly] = useState(false);

  const handleClearFilters = () => {
    setAppliedFilters({
      toolFilter: {},
      severityFilter: {},
      statusFilter: {},
      searchString: "",
    });
    setCustomSeverityOnly(false);
  };

  const toolOptions = [...new Set((controls || []).map((c) => c.tool))]
    .filter(Boolean)
    .map((tool) => ({
      name: tool,
      label: tool,
      displayName: tool,
    }));

  const severityOptions = Object.entries(RemediationTypeChip.controlPriority)
    .filter(([key]) => key !== "0")
    .map(([key, val]) => ({
      name: key,
      label: val.label,
      displayName: val.label,
    }))
    .reverse();

  const statusOptions = [
    { name: "Gap", label: "Gap", displayName: "Gap" },
    { name: "Compliant", label: "Compliant", displayName: "Compliant" },
    { name: "Snoozed", label: "Snoozed", displayName: "Snoozed" },
    { name: "Excluded", label: "Excluded", displayName: "Excluded" },
  ];

  const filteredControls = (() => {
    if (!controls) return [];
    let result = [...controls];

    const selectedTools = Object.keys(appliedFilters.toolFilter || {}).filter(
      (key) => appliedFilters.toolFilter[key]
    );
    if (selectedTools.length > 0) {
      result = result.filter((c) => selectedTools.includes(c.tool));
    }

    const selectedSeverities = Object.keys(
      appliedFilters.severityFilter || {}
    ).filter((key) => appliedFilters.severityFilter[key]);
    if (selectedSeverities.length > 0) {
      result = result.filter((c) => selectedSeverities.includes(c.severity));
    }

    const selectedStatuses = Object.keys(
      appliedFilters.statusFilter || {}
    ).filter((key) => appliedFilters.statusFilter[key]);
    if (selectedStatuses.length > 0) {
      result = result.filter((c) => {
        const statusCounts = c.status_counts || {};
        return selectedStatuses.some(
          (status) => (statusCounts[status] || 0) > 0
        );
      });
    }

    if (customSeverityOnly) {
      result = result.filter((c) =>
        (c.customers || []).some((cust) => cust.has_custom_severity)
      );
    }

    if (appliedFilters.searchString) {
      const search = appliedFilters.searchString.toLowerCase();
      result = result.filter(
        (c) =>
          c.control_id.toLowerCase().includes(search) ||
          c.title.toLowerCase().includes(search) ||
          c.tool?.toLowerCase().includes(search)
      );
    }

    return result;
  })();

  const controlsByIdColumns = [
    { field: "control_id", headerName: "Control ID", flex: 0.5 },
    {
      field: "tool",
      headerName: "Tool",
      flex: 0.5,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <IntegrationImage
              name={params.value.toLowerCase().replace(/ /g, "_")}
              width="20px"
              showToolTip={true}
              newLogo={true}
            />
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    { field: "title", headerName: "Title", flex: 2 },
    {
      field: "severity",
      headerName: "Severity",
      flex: 0.6,
      renderCell: (params) => {
        if (!params.value) return "-";
        const customers = params.row.customers || [];
        const customersWithCustomSeverity = customers.filter(
          (c) => c.has_custom_severity
        );
        const hasCustom = customersWithCustomSeverity.length > 0;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <ChipColor
              value={params.value}
              type={"controlPriority"}
              size="small"
              labelWidth={true}
            />
            {hasCustom && (
              <CustomTooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Custom Severity Changes
                    </Typography>
                    {customersWithCustomSeverity.map((c) => (
                      <Box key={c.customer_id} sx={{ fontSize: "12px" }}>
                        <strong>{c.customer_id}</strong>:{" "}
                        {RemediationTypeChip.controlPriority[
                          c.original_severity
                        ]?.label || c.original_severity}{" "}
                        →{" "}
                        {RemediationTypeChip.controlPriority[c.severity]
                          ?.label || c.severity}
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.warning,
                    fontSize: "10px",
                    fontWeight: 600,
                    cursor: "help",
                  }}
                >
                  ★
                </Typography>
              </CustomTooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "customers",
      headerName: "Customers",
      flex: 3,
      renderCell: (params) => {
        const customers = params.value || [];

        const groupedByStatus = customers.reduce((acc, customer) => {
          if (!acc[customer.status]) {
            acc[customer.status] = [];
          }
          acc[customer.status].push(customer);
          return acc;
        }, {});

        const statusOrder = ["Gap", "Snoozed", "Excluded", "Compliant"];

        return (
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            width="100%"
            py={0.5}
          >
            {statusOrder.map((status) => {
              const statusCustomers = groupedByStatus[status];
              if (!statusCustomers || statusCustomers.length === 0) return null;

              return (
                <Box key={status} display="flex" alignItems="center" gap={0.5}>
                  <StatusChip
                    label={status}
                    circleColor={getStatusIndicatorColor(status, colors)}
                  />
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      color: colors.text_primary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {statusCustomers.map((c, idx) => (
                      <span key={c.customer_id}>
                        {c.customer_id}
                        {c.has_custom_severity && (
                          <CustomTooltip
                            title={
                              <Box sx={{ fontSize: "12px" }}>
                                {RemediationTypeChip.controlPriority[
                                  c.original_severity
                                ]?.label || c.original_severity}{" "}
                                →{" "}
                                {RemediationTypeChip.controlPriority[c.severity]
                                  ?.label || c.severity}
                              </Box>
                            }
                          >
                            <span
                              style={{
                                color: colors.warning,
                                fontSize: "10px",
                                fontWeight: 600,
                                marginLeft: "2px",
                                cursor: "help",
                              }}
                            >
                              ★
                            </span>
                          </CustomTooltip>
                        )}
                        {idx < statusCustomers.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    { field: "customer_count", headerName: "# Customers", flex: 0.5 },
  ];

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.divider2}`,
        }}
      >
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Tool"
            fieldName="toolFilter"
            options={toolOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            showSearch={true}
          />
        </Box>
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Severity"
            fieldName="severityFilter"
            options={severityOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Status"
            fieldName="statusFilter"
            options={statusOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={customSeverityOnly}
              onChange={(e) => setCustomSeverityOnly(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              Custom Severity
            </Typography>
          }
          sx={{ ml: 0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search controls..."
          />
        </Box>
        <Box>
          <TextButton onClick={handleClearFilters} size="small">
            Clear
          </TextButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredControls.map((control, idx) => ({
            id: idx,
            ...control,
          }))}
          columns={controlsByIdColumns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptionsOverride={[10, 25, 50]}
          getRowHeight={() => "auto"}
          autoHeight={false}
          boxSXProps={{ height: "100%" }}
          sx={{
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default ControlsTableWidget;
